import { useEffect, useState, type RefObject } from 'react';
import { toDataURL } from 'qrcode';
import { Button, ButtonLink } from '@clay-run/glaze/button';
import { Alert, AlertDescription } from '@clay-run/glaze/alert';
import { StatusBadge, type StatusBadgeStatus } from '@clay-run/glaze/status-badge';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '@clay-run/glaze/description-list';
import { Card } from '@clay-run/glaze/card';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/index';
import { buildSubmitModalFields } from '../submit-response-fields';
import type { ScanSubmitResult } from '../scan';
import EnrichingWithClay from './EnrichingWithClay';

/**
 * The "Enriched info" section — the product owner's simplification of the
 * original `SubmitResponseModal` popup (R11/R18, U6 + U10 of the
 * lead-scanner plans): rendered IN-PAGE on `ScanReview.tsx`, below the form,
 * rather than as a centered `Modal`. No open/close lifecycle at all — the
 * caller decides whether this mounts (via `resolveEnrichedInfoState`,
 * `scan-review-logic.ts`) and passes `null` for `result` to mean "render
 * nothing", exactly like the old modal's `if (!fields) return null` except
 * the caller now owns that decision instead of an `open` boolean.
 *
 * Same three arms as before, driven by `buildSubmitModalFields`'s
 * `pending`/`status` pair — that mapper is UNCHANGED, only its consumer
 * moved out of a `Modal`:
 *
 * - **pending** — reuses `EnrichingWithClay`'s gif + "Enriching with Clay"
 *   copy (the product owner's explicit call: same branding as the
 *   LinkedIn-enrichment loading state, even though this is the submission
 *   step, not the LinkedIn lookup) instead of the old skeleton rows.
 * - **Complete | partial** — the lead reached Salesforce (never named as
 *   such to the rep — Terra never talks to Salesforce directly).
 * - **failed** — Retry (a whole new run, U9's claim cycle) and "Keep as
 *   draft".
 */

/**
 * The Lead Scanning Clay function's own table view — the routine that
 * produces every row this section renders. Hoisted to a module constant
 * (QA-fix round 4, bug 2) now that it has a single render site shared by
 * BOTH branches rather than one inline use in the terminal branch's CRM row.
 */
const LEAD_SCANNING_FUNCTION_URL =
  'https://app.clay.com/workspaces/859539/tables/t_0tkc93lsmbzQgWGEWj7/views/gv_0tkc93mAZcVjuZYQ63M';

interface EnrichedInfoSectionProps {
  containerRef?: RefObject<HTMLDivElement | null>;
  /** Just the two fields the "Lead" line shows, plus the core form's live
   *  `suppressFollowUp` toggle state (used only to seed this section's own
   *  copy of the toggle) — the caller's own local form state, not the
   *  (possibly stale) server row. */
  scan: { name: string; company: string; suppressFollowUp: boolean };
  /** Never `null` here — the caller (`ScanReview.tsx`) only mounts this
   *  component at all once `resolveEnrichedInfoState` has decided there is
   *  something to show. */
  result: ScanSubmitResult;
  campaignName: string;
  scannedByName: string;
  onRetry: () => void;
  onResend: (note: string, suppressFollowUp: boolean) => void;
  onKeepAsDraft: () => void;
  /** Disables Retry / "Add note & resend" while a submit is in flight —
   *  the same mutation backs Submit, Retry, and resend. */
  isSubmitting: boolean;
}

/**
 * Renders the GTME BOOKING link to a QR data URL via the `qrcode` package
 * (Key Technical Decision 9; the contract's `Final GTME Meeting Link` is
 * what the QR encodes per the settled §17 decision). An `<img>`, not a
 * canvas ref, so there is no paint-order effect to get StrictMode-wrong: the
 * data URL is just async state.
 *
 * No "already requested this URL" ref, same reasoning as the old modal's
 * §11.4 fix: `cancelled` alone is the correct guard. There is also no `open`
 * gate anymore — the section only ever mounts when there's something to
 * show (the caller's job, not this hook's), so there is no "hidden but still
 * mounted" state to guard against re-requesting into.
 */
function useQrDataUrl(bookingUrl: string | null): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    // Reset on the way IN, not only in cleanup — a ref/state carrying
    // lifetime meaning has to be re-initialized at the top of the effect or
    // StrictMode's setup→cleanup→setup (and any real remount) leaves the
    // previous run's value stranded on screen
    // (docs/v4-system/strictmode-resilient-effects.md).
    if (!bookingUrl) {
      setDataUrl(null);
      return;
    }
    let cancelled = false;
    void toDataURL(bookingUrl, { width: 168, margin: 1 }).then((url) => {
      if (!cancelled) setDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [bookingUrl]);

  return dataUrl;
}

/**
 * The section's title row: "Enriched info" + the status pill + the "View
 * Clay function" link.
 *
 * The link used to live inside the terminal branch's CRM row, next to "View
 * in Salesforce" — which made it reachable only AFTER the run finished, and
 * only once the rep scrolled down to that row. Live-QA round 4, bug 2: the
 * whole point of that link is watching the routine work, so it has to be
 * visible WHILE the run is in flight too. Rendering it here — one component
 * used by both the pending and terminal branches — is what guarantees the
 * two branches can't drift apart again.
 *
 * "View in Salesforce" deliberately did NOT move: that URL genuinely does
 * not exist until the row syncs, so it stays branch-local in the CRM row.
 */
function EnrichedInfoHeader({ status, label }: { status: StatusBadgeStatus; label: string }) {
  return (
    // `flex-wrap`, not the plain `flex` this row used to be: the link makes
    // the row materially wider, and lead-scanner is a phone-first app.
    <div className="flex flex-wrap items-center gap-2">
      <Heading size="xs">Enriched info</Heading>
      <StatusBadge status={status} size="xs">
        {label}
      </StatusBadge>
      <ButtonLink
        href={LEAD_SCANNING_FUNCTION_URL}
        target="_blank"
        rel="noopener noreferrer"
        variant="secondary"
        size="sm"
      >
        View Clay function
      </ButtonLink>
    </div>
  );
}

export default function EnrichedInfoSection({
  containerRef,
  scan,
  result,
  campaignName,
  scannedByName,
  onRetry,
  onResend,
  onKeepAsDraft,
  isSubmitting,
}: EnrichedInfoSectionProps) {
  const [resendNote, setResendNote] = useState('');
  const [resendSuppressFollowUp, setResendSuppressFollowUp] = useState(scan.suppressFollowUp);
  const fields = buildSubmitModalFields(scan, result, campaignName, scannedByName);
  const qrDataUrl = useQrDataUrl(fields.bookingUrl);

  const isPending = fields.pending;
  const isFailure = !isPending && fields.status === 'failed';

  function handleResendClick() {
    onResend(resendNote, resendSuppressFollowUp);
    setResendNote('');
  }

  async function handleCopyBookingLink(bookingUrl: string) {
    try {
      await navigator.clipboard.writeText(bookingUrl);
      addToast({ content: 'Booking link copied — paste it into your follow-up', status: 'success' });
    } catch (err) {
      addErrorToast({ content: 'Could not copy the booking link', error: err });
    }
  }

  if (isPending) {
    return (
      <div ref={containerRef}>
        <Card padded className="flex flex-col gap-3">
          <EnrichedInfoHeader status="action" label="submitting" />
          <EnrichingWithClay />
          <Text size="sm" variant="tertiary">
            This can take a minute. You can leave this page — it'll keep going and show as Synced in your Recent
            scans once it lands.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <Card padded className="flex flex-col gap-3">
        <EnrichedInfoHeader
          status={isFailure ? 'error' : fields.status === 'partial' ? 'warning' : 'success'}
          label={isFailure ? 'error' : 'scanned'}
        />

        {!isFailure ? (
          <>
            {fields.status === 'partial' && (
              <Alert status="warning">
                <AlertDescription>Lead captured; routing didn't complete.</AlertDescription>
              </Alert>
            )}
            <DescriptionList>
              {/* Reordered (QA-fix batch, Fix 4): "GTME owner" (formerly
                  "Routed to" — renamed since it has no other consumer, see
                  `submit-response-fields.ts`'s unrelated "Routed to ClayDR"
                  PROSE string, which lives inside `followupExplainer` and is
                  untouched by this rename) and Share/QR promoted to the top
                  — the two things a rep glances at first after a lead syncs.
                  Every row below remains independently optional; each is
                  still guarded on its own condition rather than one
                  all-or-nothing gate. */}
              {fields.gtmeName && (
                <>
                  <DescriptionTerm>GTME owner</DescriptionTerm>
                  <DescriptionDetails>{fields.gtmeName}</DescriptionDetails>
                </>
              )}
              {/* No booking link (a `partial` run, or a decision that keeps
                  the lead with an existing owner) → no Share row at all,
                  rather than an empty QR frame. */}
              {fields.bookingUrl && (
                <>
                  <DescriptionTerm>Share</DescriptionTerm>
                  <DescriptionDetails>
                    {qrDataUrl ? (
                      <div className="flex flex-col items-start gap-1">
                        <img src={qrDataUrl} alt="QR code — scan to open the booking link" width={168} height={168} />
                        <Text size="xs" variant="tertiary">
                          Scan to open the booking link
                        </Text>
                      </div>
                    ) : (
                      <Text size="xs" variant="tertiary">
                        Generating QR code…
                      </Text>
                    )}
                  </DescriptionDetails>
                </>
              )}
              <DescriptionTerm>Lead</DescriptionTerm>
              <DescriptionDetails>{fields.leadLine}</DescriptionDetails>
              <DescriptionTerm>Campaign</DescriptionTerm>
              <DescriptionDetails>{fields.campaign}</DescriptionDetails>
              <DescriptionTerm>Scanned by</DescriptionTerm>
              <DescriptionDetails>{fields.scannedBy}</DescriptionDetails>
              <DescriptionTerm>CRM</DescriptionTerm>
              <DescriptionDetails>
                {/* "View Clay function" used to sit here too (QA-fix batch,
                    Fix 5). It moved up to `EnrichedInfoHeader` in round 4 so
                    it is reachable while the run is still in flight; only
                    "View in Salesforce" is genuinely terminal-only, since
                    there is no CRM URL until the row syncs. */}
                {fields.crmUrl && (
                  <ButtonLink href={fields.crmUrl} target="_blank" rel="noopener noreferrer" variant="secondary" size="sm">
                    View in Salesforce
                  </ButtonLink>
                )}
              </DescriptionDetails>
              {fields.routingDecision && (
                <>
                  <DescriptionTerm>Routing</DescriptionTerm>
                  <DescriptionDetails>{fields.routingDecision}</DescriptionDetails>
                </>
              )}
              {/* Follow-up (the old `followupExplainer` row) was deleted here
                  (QA-fix batch, Fix 4) — no other consumer reads it; the
                  field itself stays in the wire schema, just unrendered. */}
              {fields.bookingUrl && (
                <>
                  <DescriptionTerm>Booking</DescriptionTerm>
                  <DescriptionDetails>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopyBookingLink(fields.bookingUrl as string)}
                      >
                        Copy booking link
                      </Button>
                      <ButtonLink
                        href={fields.bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="secondary"
                        size="sm"
                      >
                        Open booking link
                      </ButtonLink>
                    </div>
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>

            <div className="mt-1">
              <TextareaField
                label="Add note & resend"
                placeholder="Anything you remembered after submitting…"
                value={resendNote}
                onValueChange={setResendNote}
              />
            </div>
            <SwitchField
              label="Suppress from Follow Up"
              description="I will follow up or lead booked, suppress from marketing follow up"
              checked={resendSuppressFollowUp}
              onCheckedChange={setResendSuppressFollowUp}
            />
            <Button variant="secondary" loading={isSubmitting} onClick={handleResendClick} className="self-start">
              Add note & resend
            </Button>
          </>
        ) : (
          <>
            <DescriptionList>
              <DescriptionTerm>Lead</DescriptionTerm>
              <DescriptionDetails>{fields.leadLine}</DescriptionDetails>
            </DescriptionList>
            <Alert status="error" disableErrorLogging>
              <AlertDescription>
                {/* The run's own failure text when it carried one; the
                    prototype's generic line only when it didn't. */}
                {fields.errorMessage ?? "We couldn't reach the CRM. The scan is still saved on your phone."}
              </AlertDescription>
            </Alert>
            <Text size="sm" variant="tertiary">
              Nothing was saved to the CRM. Retry to send it again.
            </Text>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onKeepAsDraft}>
                Keep as draft
              </Button>
              <Button variant="primary" loading={isSubmitting} onClick={onRetry}>
                Retry
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
